import { useState, useMemo } from 'react';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, ReferenceArea } from 'recharts';
import type { AggregatedEngagement } from '@/types/emotion';
import { clsx } from 'clsx';

type Props = {
  data: AggregatedEngagement[];
  initialViewMode?: 'realtime' | 'flow' | 'lecture' | 'macro';
};

type ViewMode = 'realtime' | 'flow' | 'lecture' | 'macro';

const formatTimeLabel = (timestamp: number, mode: ViewMode) => {
  const date = new Date(timestamp);
  let hours = date.getUTCHours(); 
  const minutes = date.getUTCMinutes().toString().padStart(2, '0');
  const seconds = date.getUTCSeconds().toString().padStart(2, '0');
  const ampm = hours >= 12 ? 'PM' : 'AM';
  
  hours = hours % 12 || 12;

  const str = `${hours}:${minutes} ${ampm}`;
  return mode === 'realtime' ? `${str}:${seconds}` : str;
};

const MODE_TOOLTIPS: Record<ViewMode, string> = {
  realtime: "Raw Data: Updates every 2 seconds. Shows the last 60 seconds of activity.",
  flow: "Smoothing: Uses a 30-second moving average to reduce noise.",
  lecture: "Trends: Aggregates data into 5-minute buckets to show class progression.",
  macro: "Overview: Aggregates data into 15-minute buckets for long sessions."
};

export const EngagementTrendline = ({ data, initialViewMode = 'flow' }: Props) => {
  const [mode, setMode] = useState<ViewMode>(initialViewMode);
  const [hidden, setHidden] = useState({
    engaged: false, confused: false, disengaged: false, neutral: true, 
  });

  const processedData = useMemo(() => {
    if (!data?.length) return [];

    const raw = data.map(item => {
        const p = item.timestamp.split(/[-T :.Z]/).filter(Boolean).map(Number);
        const utcDate = new Date(Date.UTC(p[0], p[1] - 1, p[2], p[3] || 0, p[4] || 0, p[5] || 0));
        
        // SHIFT: -14 hours for PAKISTAN TIME
        const shiftedTimestamp = utcDate.getTime() - (19 * 60 * 60 * 1000);
        
        return { ...item, timestamp: shiftedTimestamp };
    });

    if (mode === 'realtime') return raw.slice(-30);

    let bucketSize = 30 * 1000; 
    if (mode === 'lecture') bucketSize = 5 * 60 * 1000;
    if (mode === 'macro') bucketSize = 15 * 60 * 1000;

    const buckets: Record<string, any> = {};
    raw.forEach(point => {
      const bucketKey = Math.floor(point.timestamp / bucketSize) * bucketSize;
      if (!buckets[bucketKey]) {
        buckets[bucketKey] = { 
          timestamp: bucketKey, count: 0, 
          engaged: 0, confused: 0, disengaged: 0, neutral: 0 
        };
      }
      buckets[bucketKey].engaged += point.engaged;
      buckets[bucketKey].confused += point.confused;
      buckets[bucketKey].disengaged += point.disengaged;
      buckets[bucketKey].neutral += point.neutral;
      buckets[bucketKey].count++;
    });

    return Object.values(buckets).map((b: any) => ({
      timestamp: b.timestamp,
      windowSeconds: bucketSize / 1000,
      engaged: b.engaged / b.count,
      confused: b.confused / b.count,
      disengaged: b.disengaged / b.count,
      neutral: b.neutral / b.count,
    })).sort((a, b) => a.timestamp - b.timestamp);

  }, [data, mode]);

  const toggleLine = (key: keyof typeof hidden) => {
    setHidden(prev => ({ ...prev, [key]: !prev[key] }));
  };

  if (!data?.length) {
    return <div className="rounded-xl border border-background-border bg-background-surface p-6 text-sm text-text-dimmed">No data yet</div>;
  }

  return (
    <div className="rounded-2xl border border-background-border bg-background-surface p-6 shadow-xl shadow-glow">
      <div className="flex flex-col gap-4 mb-6 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-sm font-semibold text-text-primary">Engagement Timeline</p>
          <p className="text-xs text-text-dimmed mt-1">Timezone: Pakistan Standard Time</p>
        </div>
        
        <div className="flex bg-background-main p-1 rounded-lg border border-background-border">
          {(['realtime', 'flow', 'lecture', 'macro'] as ViewMode[]).map((m) => (
            <button
              key={m}
              onClick={() => setMode(m)}
              title={MODE_TOOLTIPS[m]}
              className={clsx(
                "px-3 py-1 text-xs font-medium rounded-md transition-all capitalize",
                mode === m ? "bg-background-border text-text-primary shadow-sm ring-1 ring-brand/30" : "text-text-dimmed hover:text-text-primary hover:bg-background-border"
              )}
            >
              {m === 'realtime' ? '⚡ Live' : m === 'flow' ? '🌊 Flow' : m === 'lecture' ? '🎓 Lecture' : '🌎 Macro'}
            </button>
          ))}
        </div>
      </div>

      <div className="h-72 w-full">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={processedData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#27272A" vertical={false} />
            <XAxis dataKey="timestamp" tickFormatter={(t) => formatTimeLabel(t, mode)} type="number" domain={['dataMin', 'dataMax']} stroke="#A1A1AA" fontSize={11} tickMargin={10} />
            <YAxis tickFormatter={(value) => `${Math.round(value * 100)}%`} stroke="#A1A1AA" fontSize={11} domain={[0, 1]} />
            <Tooltip contentStyle={{ backgroundColor: '#18181B', borderColor: '#27272A', color: '#F4F4F5' }} labelFormatter={(t) => formatTimeLabel(t as number, mode)} formatter={(value: number) => [`${Math.round(value * 100)}%`]} />
            {!hidden.engaged && <Line type="monotone" dataKey="engaged" stroke="#22c55e" strokeWidth={2} dot={false} animationDuration={500} />}
            {!hidden.confused && <Line type="monotone" dataKey="confused" stroke="#f97316" strokeWidth={2} dot={false} animationDuration={500} />}
            {!hidden.disengaged && <Line type="monotone" dataKey="disengaged" stroke="#ef4444" strokeWidth={2} dot={false} animationDuration={500} />}
            {!hidden.neutral && <Line type="monotone" dataKey="neutral" stroke="#A1A1AA" strokeWidth={2} dot={false} animationDuration={500} />}
            {/*{mode !== 'realtime' && processedData.filter(d => d.engaged < 0.4).map((point, i) => (           THIS IS ORANGE HEATMAP DISABLED FOR NOW
               <ReferenceArea key={i} x1={point.timestamp - (point.windowSeconds * 500)} x2={point.timestamp + (point.windowSeconds * 500)} fill="#f97316" fillOpacity={0.05} />
            ))}*/}
          </LineChart>
        </ResponsiveContainer>
      </div>

      <div className="mt-4 flex flex-wrap justify-center gap-4 border-t border-background-border pt-4">
        {[
          { key: 'engaged', color: 'bg-green-500', label: 'Engaged' },
          { key: 'confused', color: 'bg-orange-500', label: 'Confused' },
          { key: 'disengaged', color: 'bg-red-500', label: 'Disengaged' },
          { key: 'neutral', color: 'bg-text-dimmed', label: 'Neutral' },
        ].map((item) => (
          <button
            key={item.key}
            onClick={() => toggleLine(item.key as keyof typeof hidden)}
            className={clsx(
              "flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium transition-all border",
              hidden[item.key as keyof typeof hidden] ? 'bg-background-main/50 border-background-border text-text-dimmed opacity-60' : 'bg-background-main border-background-border text-text-primary hover:border-brand'
            )}
          >
            <span className={clsx("h-2 w-2 rounded-full", item.color, hidden[item.key as keyof typeof hidden] && 'grayscale')} />
            {item.label}
          </button>
        ))}
      </div>
    </div>
  );
};